<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="component"></column-header>
    <div class="tabulator-parent">
      <p v-if="loading && !subscription"><i class="fa fa-fw fa-spinner fa-spin"></i> {{ _('Loading...') }}</p>

      <!-- Existing subscription -->
      <template v-else-if="subscription">
        <div class="row">
          <div class="col-md-12">
            <h3>{{ subscription.name }} <span :class="'label label-' + statusClass(subscription)">{{ statusName(subscription) }}</span></h3>
          </div>
        </div>
        <div class="alert alert-warning" v-if="subscription.cancelled && subscription.status !== 'ended'">
          {{ _('The subscription has been cancelled and ends on %s.').replace('%s', formatLastDay(subscription.ends_at_ts || subscription.valid_until_ts)) }}
        </div>
        <div class="alert alert-danger" v-if="subscription.status === 'suspended'">
          {{ _('The subscription is suspended.') }}
          <template v-if="subscription.payment_due_ts">{{ _('The payment was due on %s.').replace('%s', formatDate(subscription.payment_due_ts)) }}</template>
          {{ _('It is resumed automatically when the payment is received.') }}
        </div>
        <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
        <div class="tab-content">
          <div v-bind="tabPanel('general')" class="tab-pane active" v-if="isTabActive('general')">
            <div class="row">
              <div class="col-lg-6">
                <table class="table">
                  <tbody>
                    <tr>
                      <td width="45%">{{ _('Interval') }}</td>
                      <td>{{ intervalName(subscription.interval, subscription.interval_count) }}</td>
                    </tr>
                    <tr v-if="subscription.billingcustomer_id">
                      <td>{{ _('Billing customer') }}</td>
                      <td><a href="javascript:;" v-on:click="openBillingCustomer(subscription.billingcustomer_id)">{{ subscription.billingcustomer_name || subscription.billingcustomer_id }}</a><template v-if="subscription.reference"> ({{ subscription.reference }})</template></td>
                    </tr>
                    <tr v-if="subscription.customer_id">
                      <td>{{ _('Customer') }}</td>
                      <td><a href="javascript:;" v-on:click="openCustomer(subscription.customer_id)">{{ subscription.customer_name || subscription.customer_id }}</a></td>
                    </tr>
                    <tr v-if="subscription.email">
                      <td>{{ _('Email') }}</td>
                      <td>{{ subscription.email }}</td>
                    </tr>
                    <tr>
                      <td>{{ _('Started') }}</td>
                      <td>{{ formatDate(subscription.start_ts) }}</td>
                    </tr>
                    <tr v-if="subscription.period_start_ts">
                      <td>{{ _('Current period') }}</td>
                      <td>{{ formatDate(subscription.period_start_ts) }}–{{ formatLastDay(subscription.period_end_ts) }}</td>
                    </tr>
                    <tr>
                      <td>{{ _('Valid until') }}</td>
                      <td>{{ formatLastDay(subscription.valid_until_ts) }}</td>
                    </tr>
                    <tr v-if="subscription.next_billing_ts">
                      <td>{{ _('Next billing') }}</td>
                      <td>{{ formatDate(subscription.next_billing_ts) }}</td>
                    </tr>
                    <tr v-if="subscription.term_end_ts">
                      <td>{{ _('Fixed term ends') }}</td>
                      <td>{{ formatLastDay(subscription.term_end_ts) }}</td>
                    </tr>
                    <tr v-if="subscription.ended_ts">
                      <td>{{ _('Ended') }}</td>
                      <td>{{ formatDate(subscription.ended_ts) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="col-lg-6">
                <table class="table">
                  <tbody>
                    <tr>
                      <td width="45%">{{ _('Minimum term') }}</td>
                      <td>{{ subscription.contract_periods }} × {{ intervalName(subscription.interval, subscription.interval_count) }}</td>
                    </tr>
                    <tr v-if="subscription.cancellation_rule_end_ts && !subscription.cancelled">
                      <td>{{ _('If cancelled now, ends on') }}</td>
                      <td>{{ formatLastDay(subscription.cancellation_rule_end_ts) }}</td>
                    </tr>
                    <tr>
                      <td>{{ _('Customer can cancel') }}</td>
                      <td>{{ subscription.customer_can_cancel ? _('Yes') : _('No') }}</td>
                    </tr>
                    <tr v-if="subscription.cancelled_ts">
                      <td>{{ _('Cancelled') }}</td>
                      <td>{{ formatDate(subscription.cancelled_ts) }}<template v-if="cancelledByName">, {{ cancelledByName }}</template></td>
                    </tr>
                  </tbody>
                </table>
                <div class="form-group" v-if="subscription.status !== 'ended'">
                  <label for="subscriptionPayment">{{ _('Renewals are paid by') }}</label>
                  <div class="input-group">
                    <select class="form-control" id="subscriptionPayment" v-model="payment">
                      <option value="bill">{{ _('Invoice') }}</option>
                      <option value="stored" v-if="subscription.charger">{{ storedPaymentText }}</option>
                      <option value="link">{{ _('Payment link') }}</option>
                    </select>
                    <span class="input-group-btn">
                      <button class="btn btn-default" type="button" v-on:click="savePayment" :disabled="busy || payment === currentPayment"><i class="fa fa-fw fa-save"></i> {{ _('Save') }}</button>
                    </span>
                  </div>
                  <p class="text-danger" v-if="currentPayment === 'stored' && subscription.charger_failed_ts">
                    {{ _('The automatic payment failed on %s. A payment link was sent to the customer.').replace('%s', formatDate(subscription.charger_failed_ts)) }}
                  </p>
                  <div v-if="confirmingRemovePayment" class="alert alert-warning">
                    <p>{{ _('Renewals will be paid by payment link. The customer can set up automatic payment again on the order page.') }}</p>
                    <button class="btn btn-danger" type="button" v-on:click="removePayment" :disabled="busy">{{ _('Remove stored payment method') }}</button>
                    <button class="btn btn-default" type="button" v-on:click="cancelRemovePayment" :disabled="busy">{{ _('Back') }}</button>
                  </div>
                </div>
              </div>
            </div>

            <h3>{{ _('Rows') }}</h3>
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ _('Product name') }}</th>
                    <th>{{ _('Price per period (inc. vat)') }}</th>
                    <th>{{ _('VAT %') }}</th>
                    <th width="140">{{ _('Amount') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in subscription.rows" :key="row.id">
                    <td>{{ row.product_name }}</td>
                    <td>{{ formatPrice(row.period_price) }}</td>
                    <td>{{ formatVat(row.vat, numberFormat) }}</td>
                    <td>
                      <input v-if="subscription.status !== 'ended'" type="number" min="0" class="form-control input-sm" v-model.number="rowCounts[row.id]" :aria-label="_('Amount')" />
                      <template v-else>{{ row.count }}</template>
                      <p class="help-block" v-if="row.scheduled_count !== null">{{ _('%s from the next period').replace('%s', row.scheduled_count) }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="countsChanged" class="help-block">{{ _('Added units are charged for the rest of the current period on the next bill. Removed units are taken off from the next period, with no refunds.') }}</p>
            <button v-if="countsChanged" class="btn btn-success" type="button" v-on:click="saveCounts" :disabled="busy"><i class="fa fa-fw fa-save"></i> {{ _('Save amounts') }}</button>

            <template v-if="subscription.pendingcharges && subscription.pendingcharges.length">
              <h3>{{ _('Charges for the next bill') }}</h3>
              <div class="table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>{{ _('Product name') }}</th>
                      <th>{{ _('Amount') }}</th>
                      <th>{{ _('Unit price (inc. vat)') }}</th>
                      <th>{{ _('Due date') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in subscription.pendingcharges" :key="c.id">
                      <td>{{ c.product_name }}</td>
                      <td>{{ c.count }}</td>
                      <td>{{ formatPrice(c.price) }}</td>
                      <td>{{ formatDate(c.due_ts) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>

          <div v-bind="tabPanel('changes')" class="tab-pane active" v-if="isTabActive('changes')">
            <p v-if="!subscription.changes || !subscription.changes.length" class="text-muted">{{ _('No changes.') }}</p>
            <div class="table-responsive" v-else>
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ _('Time') }}</th>
                    <th>{{ _('Change') }}</th>
                    <th>{{ _('Amount') }}</th>
                    <th>{{ _('Order') }}</th>
                    <th>{{ _('User') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in subscription.changes" :key="i">
                    <td>{{ formatDateTime(c.ts) }}</td>
                    <td>{{ changeName(c) }}</td>
                    <td><template v-if="c.amount">{{ formatPrice(c.amount) }}</template></td>
                    <td><a v-if="c.order_id" href="javascript:;" v-on:click="openOrder(c.order_id)">{{ c.order_id }}</a></td>
                    <td>{{ c.user_email }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-bind="tabPanel('orders')" class="tab-pane active" v-if="isTabActive('orders')">
            <p v-if="!subscription.orders || !subscription.orders.length" class="text-muted">{{ _('No orders.') }}</p>
            <div class="table-responsive" v-else>
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>{{ _('Order no.') }}</th>
                    <th>{{ _('Order date') }}</th>
                    <th>{{ _('Total') }}</th>
                    <th>{{ _('Status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="o in subscription.orders" :key="o.id" class="clickable" v-on:click="openOrder(o.id)">
                    <td>{{ o.id }}</td>
                    <td>{{ formatDate(o.orderdate) }}</td>
                    <td>{{ formatPrice(o.total) }}</td>
                    <td>{{ parseInt(o.paid) ? _('Paid') : _('Not paid') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <!-- New subscription -->
      <template v-else>
        <div class="row">
          <div class="col-md-12">
            <h3>{{ _('New subscription') }}</h3>
            <p class="help-block">{{ _('Create a subscription by hand, for example to move recurring charges over from another billing system. Recurring bills are created automatically.') }}</p>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label>{{ _('Payer') }}</label>
              <div class="radio">
                <label><input type="radio" value="billingcustomer" v-model="form.payerType" /> {{ _('Billing customer') }}</label>
              </div>
              <div class="radio">
                <label><input type="radio" value="customer" v-model="form.payerType" /> {{ _('Customer') }}</label>
              </div>
            </div>
            <div class="form-group" v-if="form.payerType === 'billingcustomer'">
              <label>{{ _('Billing customer') }}</label>
              <model-list-select :list="billingCustomers" option-value="id" option-text="company" :custom-text="formatBillingCustomer" v-model="form.billingcustomer_id" :placeholder="_('Type to search customers')"></model-list-select>
            </div>
            <div class="form-group" v-else>
              <label>{{ _('Customer') }}</label>
              <model-list-select :list="customers" option-value="id" option-text="name" v-model="form.customer_id" :placeholder="_('Type to search customers')"></model-list-select>
            </div>
            <div class="form-group">
              <label for="newSubscriptionName">{{ _('Name') }}</label>
              <input type="text" class="form-control" id="newSubscriptionName" v-model="form.name" :placeholder="_('Defaults to the first row\'s product name')" />
            </div>
            <div class="form-group">
              <label for="newSubscriptionEmail">{{ _('Email') }}</label>
              <input type="email" class="form-control" id="newSubscriptionEmail" v-model="form.email" :placeholder="_('Defaults to the payer\'s email')" />
            </div>
            <div class="form-group">
              <label for="newSubscriptionPayment">{{ _('Renewals are paid by') }}</label>
              <select class="form-control" id="newSubscriptionPayment" v-model="form.payment">
                <option value="bill">{{ _('Invoice') }}</option>
                <option value="link">{{ _('Payment link') }}</option>
              </select>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-group">
              <label>{{ _('Interval') }}</label>
              <div class="flex-row" style="gap: 10px;">
                <input type="number" min="1" class="form-control" style="max-width: 100px;" v-model.number="form.interval_count" :aria-label="_('Interval count')" />
                <select class="form-control" v-model="form.interval" :aria-label="_('Interval')">
                  <option value="day">{{ _('days') }}</option>
                  <option value="week">{{ _('weeks') }}</option>
                  <option value="month">{{ _('months') }}</option>
                  <option value="year">{{ _('years') }}</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label>{{ _('Start date') }}</label>
              <DatePicker v-model="startDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
              <p class="help-block">{{ _('Periods start on this day of the month or year.') }}</p>
            </div>
            <div class="checkbox">
              <label><input type="checkbox" v-model="form.bill_first" /> {{ _('Bill the first period now') }}</label>
              <p class="help-block">{{ _('Leave unchecked if the time before the start date has already been billed elsewhere. The first bill is then created on the start date.') }}</p>
            </div>
            <div class="form-group">
              <label for="newSubscriptionContract">{{ _('Minimum term (periods)') }}</label>
              <input type="number" min="1" class="form-control" id="newSubscriptionContract" v-model.number="form.contract_periods" />
            </div>
            <div class="form-group">
              <label for="newSubscriptionCancellation">{{ _('Cancellation') }}</label>
              <select class="form-control" id="newSubscriptionCancellation" v-model="form.cancellation">
                <option value="contract_end">{{ _('Ends at the end of the current term') }}</option>
                <option value="next_contract_end">{{ _('Ends at the end of the next term') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ _('Fixed term ends (optional)') }}</label>
              <DatePicker v-model="termEndDate" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
            </div>
            <div class="checkbox">
              <label><input type="checkbox" v-model="form.customer_can_cancel" /> {{ _('Customer can cancel on the order page') }}</label>
            </div>
          </div>
        </div>
        <h3>{{ _('Rows') }}</h3>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>{{ _('Product name') }}</th>
                <th width="150">{{ _('Price per period (inc. vat)') }}</th>
                <th width="100">{{ _('VAT %') }}</th>
                <th width="100">{{ _('Amount') }}</th>
                <th width="50"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in form.rows" :key="index">
                <td><input type="text" class="form-control" v-model="row.product_name" :aria-label="_('Product name')" /></td>
                <td><input type="number" step="0.01" class="form-control" v-model="row.price" :aria-label="_('Price per period (inc. vat)')" /></td>
                <td><input type="number" step="0.1" class="form-control" v-model="row.vat" :aria-label="_('VAT %')" /></td>
                <td><input type="number" min="1" class="form-control" v-model.number="row.count" :aria-label="_('Amount')" /></td>
                <td><button class="btn btn-danger" type="button" v-on:click="form.rows.splice(index, 1)" :disabled="form.rows.length < 2" :title="_('Delete')"><i class="fa fa-fw fa-trash"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <button class="btn btn-default" type="button" v-on:click="addRow"><i class="fa fa-fw fa-plus"></i> {{ _('Add row') }}</button>
      </template>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="flex-row" style="gap: 5px;">
          <template v-if="subscription && subscription.status !== 'ended'">
            <button v-if="!subscription.cancelled" class="btn btn-warning" type="button" v-on:click="cancelDialog" :disabled="busy"><i class="fa fa-fw fa-ban"></i><span class="hidden-xs"> {{ _('Cancel subscription') }}</span></button>
            <button v-else class="btn btn-success" type="button" v-on:click="runAction('uncancel')" :disabled="busy"><i class="fa fa-fw fa-undo"></i><span class="hidden-xs"> {{ _('Undo cancellation') }}</span></button>
            <button v-if="subscription.status === 'active'" class="btn btn-danger" type="button" v-on:click="suspendDialog" :disabled="busy"><i class="fa fa-fw fa-pause"></i><span class="hidden-xs"> {{ _('Suspend') }}</span></button>
            <button v-if="subscription.status === 'suspended'" class="btn btn-success" type="button" v-on:click="runAction('resume')" :disabled="busy"><i class="fa fa-fw fa-play"></i><span class="hidden-xs"> {{ _('Resume') }}</span></button>
          </template>
        </div>
        <div class="button-col flex-row">
          <button v-if="!subscription && !loading" class="btn btn-success" v-on:click="create" :disabled="busy || !isFormValid"><i class="fa fa-fw fa-save"></i> {{ _('Create subscription') }}</button>
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import formats from '~/lib/formats'
import subscriptionhelpers from '~/lib/subscriptionhelpers'
import { DateTime } from 'luxon'
import { ModelListSelect } from 'vue-search-select'

const emptyRow = () => ({ product_name: '', price: '', vat: '', count: 1 })

export default {
  data () {
    return {
      subscription: null,
      subscriptionId: null,
      busy: false,
      payment: 'bill',
      confirmingRemovePayment: false,
      rowCounts: {},
      billingCustomers: [],
      customers: [],
      form: {
        payerType: 'billingcustomer',
        billingcustomer_id: null,
        customer_id: null,
        name: '',
        email: '',
        payment: 'bill',
        interval: 'month',
        interval_count: 1,
        start_date: DateTime.now().plus({ months: 1 }).startOf('month').toISODate(),
        bill_first: false,
        contract_periods: 1,
        cancellation: 'contract_end',
        term_end_date: null,
        customer_can_cancel: false,
        rows: [emptyRow()]
      }
    }
  },
  mixins: [components, formats, subscriptionhelpers],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'changes', label: this._('Change log') },
        { id: 'orders', label: this._('Orders'), badge: this.subscription.orders && this.subscription.orders.length ? this.subscription.orders.length : null }
      ])
    },
    currentPayment () {
      const subscription = this.subscription
      if (!subscription) {
        return 'bill'
      }
      return subscription.payment === 'link' && subscription.charger ? 'stored' : subscription.payment
    },
    storedPaymentText () {
      const subscription = this.subscription
      return this._('Automatic payment') + ' (' + (subscription.charger_label || this.chargerName(subscription.charger)) + ')'
    },
    startDate: {
      get () {
        return this.form.start_date ? DateTime.fromISO(this.form.start_date).toJSDate() : null
      },
      set (value) {
        this.form.start_date = value ? DateTime.fromJSDate(value).toISODate() : null
      }
    },
    termEndDate: {
      get () {
        return this.form.term_end_date ? DateTime.fromISO(this.form.term_end_date).toJSDate() : null
      },
      set (value) {
        this.form.term_end_date = value ? DateTime.fromJSDate(value).toISODate() : null
      }
    },
    countsChanged () {
      if (!this.subscription || !this.subscription.rows) {
        return false
      }
      return this.subscription.rows.some(row => this.isValidCount(this.rowCounts[row.id]) && this.rowCounts[row.id] !== this.targetCount(row))
    },
    cancelledByName () {
      switch (this.subscription ? this.subscription.cancelled_by : '') {
        case 'customer':
          return this._('by the customer')
        case 'merchant':
          return this._('by the merchant')
        case 'system':
          return this._('automatically')
        default:
          return ''
      }
    },
    isFormValid () {
      const payer = this.form.payerType === 'billingcustomer' ? this.form.billingcustomer_id : this.form.customer_id
      if (!payer || !this.form.start_date || !(parseInt(this.form.interval_count, 10) >= 1)) {
        return false
      }
      const rows = this.form.rows.filter(r => r.product_name && r.product_name.trim())
      return rows.length > 0 && rows.every(r => r.price !== '' && !isNaN(parseFloat(r.price)) && r.vat !== '' && !isNaN(parseFloat(r.vat)) && parseInt(r.count, 10) >= 1)
    }
  },
  methods: {
    isValidCount (n) {
      return Number.isInteger(n) && n >= 0
    },
    targetCount (row) {
      return row.scheduled_count !== null ? row.scheduled_count : row.count
    },
    changeName (change) {
      switch (change.type) {
        case 'create':
          return this._('Created')
        case 'increase':
          return this._('Amount changed: %s').replace('%s', change.from_count + ' → ' + change.to_count)
        case 'schedule':
          return this._('Amount changes from the next period: %s').replace('%s', change.from_count + ' → ' + change.to_count)
        case 'unchanged':
          return this._('Amount set to %s').replace('%s', change.to_count)
        case 'renew':
          return this._('Renewed')
        case 'cancel':
          return this._('Cancelled')
        case 'uncancel':
          return this._('Cancellation undone')
        case 'suspend':
          return this._('Suspended')
        case 'resume':
          return this._('Resumed')
        case 'end':
          return this._('Ended')
        default:
          return change.type
      }
    },
    async loadSubscription (id) {
      this.loading = true
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/subscriptions/' + id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.loading = false
        this.showError(e)
        return
      }
      this.setSubscription(response.data)
      this.loading = false
    },
    setSubscription (subscription) {
      this.subscription = subscription
      this.subscriptionId = subscription.id
      this.payment = subscription.payment === 'link' && subscription.charger ? 'stored' : subscription.payment
      const counts = {}
      for (const row of subscription.rows || []) {
        counts[row.id] = this.targetCount(row)
      }
      this.rowCounts = counts
    },
    async patch (data) {
      this.busy = true
      try {
        await axios.patch(this.baseDomain + this.shopId + '/subscriptions/' + this.subscriptionId + '.json?apikey=' + this.apiKey, data)
      } catch (e) {
        this.busy = false
        this.showError(e)
        return false
      }
      await this.loadSubscription(this.subscriptionId)
      this.busy = false
      this.EventBus.$emit('subscriptionUpdate', { id: this.subscriptionId })
      this.showNotification(this._('Changes saved successfully!'))
      return true
    },
    runAction (action, extra = {}) {
      return this.patch({ action, ...extra })
    },
    savePayment () {
      if (this.payment === 'link' && this.subscription.charger) {
        this.confirmingRemovePayment = true
        return
      }
      this.patch({ payment: this.payment === 'stored' ? 'link' : this.payment })
    },
    async removePayment () {
      if (this.subscription.payment !== 'link' && !(await this.patch({ payment: 'link' }))) {
        return
      }
      if (await this.patch({ action: 'remove_payment' })) {
        this.confirmingRemovePayment = false
      }
    },
    cancelRemovePayment () {
      this.confirmingRemovePayment = false
      this.payment = this.currentPayment
    },
    chargerName (charger) {
      return charger === 'mobilepay' ? 'MobilePay' : this._('Card')
    },
    saveCounts () {
      const rows = this.subscription.rows
        .filter(row => this.isValidCount(this.rowCounts[row.id]) && this.rowCounts[row.id] !== this.targetCount(row))
        .map(row => ({ id: row.id, count: this.rowCounts[row.id] }))
      if (rows.length) {
        this.patch({ rows })
      }
    },
    cancelDialog () {
      const s = this.subscription
      const options = []
      if (s.cancellation_rule_end_ts) {
        options.push({ value: 'rule', name: this._('By the cancellation rule, ends on %s').replace('%s', this.formatLastDay(s.cancellation_rule_end_ts)) })
      }
      if (s.period_end_ts) {
        options.push({ value: 'period_end', name: this._('At the end of the current period, ends on %s').replace('%s', this.formatLastDay(s.period_end_ts)) })
      }
      options.push({ value: 'date', name: this._('On a date I choose') })
      options.push({ value: 'immediate', name: this._('Immediately') })
      this.showDialog({
        title: this._('Cancel subscription'),
        text: this._('Periods that have already been billed are not refunded.'),
        width: 700,
        buttons: [
          {
            title: this._('Back'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Cancel subscription'),
            class: 'warning',
            icon: 'ban',
            requiredFields: ['mode'],
            handler: (data) => {
              if (data.mode === 'date' && !data.date) {
                return this.showError(this._('Please select the end date.'))
              }
              this.runAction('cancel', {
                mode: data.mode,
                date: data.mode === 'date' && data.date ? DateTime.fromJSDate(data.date).toISODate() : null,
                notify: parseInt(data.notify, 10) === 1
              })
            }
          }
        ],
        fields: [
          { title: this._('The subscription ends'), type: 'select', name: 'mode', options, defaultValue: options[0].value },
          { title: this._('Last valid day'), type: 'date', name: 'date', condition: 'mode', conditionValue: 'date' },
          { title: this._('Send a cancellation email to the customer'), type: 'checkbox', name: 'notify', defaultValue: '1' }
        ]
      })
    },
    suspendDialog () {
      this.showDialog({
        title: this._('Suspend subscription'),
        text: this._('The subscription stops right away and is not renewed until it is resumed. Access given by the subscription ends too.'),
        buttons: [
          {
            title: this._('Back'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Suspend'),
            class: 'danger',
            icon: 'pause',
            handler: () => this.runAction('suspend')
          }
        ]
      })
    },
    addRow () {
      this.form.rows.push(emptyRow())
    },
    formatBillingCustomer (bc) {
      if (!bc || !bc.id) {
        return ''
      }
      return [bc.company, bc.name].filter(Boolean).join(' - ') || String(bc.id)
    },
    async loadPayers () {
      try {
        const [billingCustomers, customers] = await Promise.all([
          axios.get(this.baseDomain + this.shopId + '/billingcustomers.json?apikey=' + this.apiKey),
          axios.get(this.baseDomain + this.shopId + '/customers.json?apikey=' + this.apiKey)
        ])
        this.billingCustomers = Object.values(billingCustomers.data || {}).map(bc => ({ ...bc, id: String(bc.id) }))
        this.customers = Object.values(customers.data || {}).map(c => ({ ...c, id: String(c.id) }))
      } catch (e) {
        this.showError(e)
      }
    },
    async create () {
      const f = this.form
      const data = {
        name: f.name,
        email: f.email,
        payment: f.payment,
        interval: f.interval,
        interval_count: f.interval_count,
        start_date: f.start_date,
        bill_first: f.bill_first,
        contract_periods: f.contract_periods,
        cancellation: f.cancellation,
        term_end_date: f.term_end_date,
        customer_can_cancel: f.customer_can_cancel ? 1 : 0,
        rows: f.rows.filter(r => r.product_name && r.product_name.trim())
      }
      if (f.payerType === 'billingcustomer') {
        data.billingcustomer_id = f.billingcustomer_id
      } else {
        data.customer_id = f.customer_id
      }
      this.busy = true
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/subscriptions.json?apikey=' + this.apiKey, data)
      } catch (e) {
        this.busy = false
        return this.showError(e)
      }
      this.busy = false
      this.showNotification(this._('Subscription created.'))
      this.EventBus.$emit('subscriptionUpdate', { id: response.data })
      await this.loadSubscription(response.data)
    },
    openOrder (id) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ' ' + id,
        width: 6,
        data: {
          orderId: id,
          trail: this.componentTrail
        }
      })
    },
    openBillingCustomer (id) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'BillingCustomerDetails',
        title: this._('Billing Customer') + ' ' + id,
        width: 6,
        data: {
          billingCustomerId: id,
          trail: this.componentTrail
        }
      })
    },
    openCustomer (id) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CustomerDetails',
        title: this._('Customer') + ' ' + id,
        width: 6,
        data: {
          customerId: id,
          trail: this.componentTrail
        }
      })
    },
    init () {
      const initData = this.component && this.component.initData ? this.component.initData : {}
      if (parseInt(initData.subscriptionId, 10)) {
        if (parseInt(initData.subscriptionId, 10) !== parseInt(this.subscriptionId, 10)) {
          this.subscription = null
          this.loadSubscription(initData.subscriptionId)
        }
        return
      }
      this.subscription = null
      this.subscriptionId = null
      if (parseInt(initData.billingCustomerId, 10)) {
        this.form.payerType = 'billingcustomer'
        this.form.billingcustomer_id = String(parseInt(initData.billingCustomerId, 10))
      } else if (parseInt(initData.customerId, 10)) {
        this.form.payerType = 'customer'
        this.form.customer_id = String(parseInt(initData.customerId, 10))
      }
      this.loadPayers()
    }
  },
  components: {
    ModelListSelect
  },
  watch: {
    'component.initData': {
      handler () {
        this.init()
      },
      deep: true
    }
  },
  created () {
    this.init()
  }
}
</script>
